import {
  type CreateFinanceActivityInput,
  type FinanceActivitiesListQuery,
  type FinanceActivity,
  type UpdateFinanceActivityInput,
} from "@/features/finance/model/finance-types"
import {
  http,
} from "@/shared/lib/api/http"
import {
  extractListPayload,
  normalizeActivityStatus,
  normalizeBoolean,
  normalizeNullableString,
  normalizeIncomeCategorySummary,
  normalizeMemberSummary,
  normalizeString,
} from "./finance-api-shared"


export function normalizeActivity(input: unknown): FinanceActivity {
  const record = (input ?? {}) as Record<string, unknown>

  return {
    id: normalizeString(record.id) ?? "",
    activityId: normalizeNullableString(record.activityId ?? record.activity_id),
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeNullableString(record.branchId ?? record.branch_id),
    collectsFunds: normalizeBoolean(record.collectsFunds ?? record.collects_funds),
    incomeCategoryId: normalizeNullableString(record.incomeCategoryId ?? record.income_category_id),
    fundId: normalizeNullableString(record.fundId ?? record.fund_id),
    groupId: normalizeNullableString(record.groupId ?? record.group_id),
    ministryId: normalizeNullableString(record.ministryId ?? record.ministry_id),
    title: normalizeString(record.title) ?? "",
    description: normalizeNullableString(record.description),
    status: normalizeActivityStatus(record.status),
    startsAt: normalizeString(record.startsAt ?? record.starts_at) ?? new Date().toISOString(),
    endsAt: normalizeString(record.endsAt ?? record.ends_at) ?? new Date().toISOString(),
    responsibleMemberId: normalizeNullableString(record.responsibleMemberId ?? record.responsible_member_id),
    incomeCategory: normalizeIncomeCategorySummary(record.incomeCategory ?? record.income_category),
    responsibleMember: normalizeMemberSummary(record.responsibleMember ?? record.responsible_member),
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}


function buildActivitiesParams(query: FinanceActivitiesListQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    workspaceId: query.workspaceId,
  }

  if (query.branchId) params.branchId = query.branchId
  if (query.incomeCategoryId) params.incomeCategoryId = query.incomeCategoryId
  if (query.fundId) params.fundId = query.fundId
  if (query.groupId) params.groupId = query.groupId
  if (query.ministryId) params.ministryId = query.ministryId
  if (query.status) params.status = query.status
  if (typeof query.activeOnly === "boolean") params.activeOnly = query.activeOnly
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = Math.min(query.limit, 100)

  return params
}


export async function listFinanceActivities(query: FinanceActivitiesListQuery): Promise<FinanceActivity[]> {
  const response = await http.get<unknown>("/finance/activities", {
    params: buildActivitiesParams(query),
  })

  return extractListPayload(response.data, ["items", "activities"], normalizeActivity)
}


export async function getFinanceActivity(id: string): Promise<FinanceActivity> {
  const response = await http.get<unknown>(`/finance/activities/${id}`)
  return normalizeActivity(response.data)
}


export async function createFinanceActivity(input: CreateFinanceActivityInput): Promise<FinanceActivity> {
  const response = await http.post<unknown>("/finance/activities", {
    workspaceId: input.workspaceId,
    branchId: input.branchId ?? null,
    collectsFunds: input.collectsFunds ?? false,
    incomeCategoryId: input.collectsFunds ? input.incomeCategoryId ?? null : null,
    fundId: input.fundId ?? null,
    groupId: input.groupId ?? null,
    ministryId: input.ministryId ?? null,
    title: input.title,
    description: input.description ?? null,
    status: input.status,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    responsibleMemberId: input.responsibleMemberId ?? null,
  })
  return normalizeActivity(response.data)
}


export async function updateFinanceActivity(input: UpdateFinanceActivityInput): Promise<FinanceActivity> {
  const payload: Record<string, unknown> = {}
  if (input.patch.branchId !== undefined) payload.branchId = input.patch.branchId ?? null
  if (input.patch.collectsFunds !== undefined) payload.collectsFunds = input.patch.collectsFunds
  if (input.patch.incomeCategoryId !== undefined) payload.incomeCategoryId = input.patch.incomeCategoryId ?? null
  if (input.patch.fundId !== undefined) payload.fundId = input.patch.fundId ?? null
  if (input.patch.groupId !== undefined) payload.groupId = input.patch.groupId ?? null
  if (input.patch.ministryId !== undefined) payload.ministryId = input.patch.ministryId ?? null
  if (input.patch.title !== undefined) payload.title = input.patch.title
  if (input.patch.description !== undefined) payload.description = input.patch.description ?? null
  if (input.patch.status !== undefined) payload.status = input.patch.status
  if (input.patch.startsAt !== undefined) payload.startsAt = input.patch.startsAt
  if (input.patch.endsAt !== undefined) payload.endsAt = input.patch.endsAt
  if (input.patch.responsibleMemberId !== undefined) {
    payload.responsibleMemberId = input.patch.responsibleMemberId ?? null
  }

  const response = await http.patch<unknown>(`/finance/activities/${input.id}`, payload)
  return normalizeActivity(response.data)
}
